import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'

// Actions
import { actions as collectionsActions } from 'modules/collections/actions'

// Components
import { EmptyState, Entity, IconCollectionLarge } from '@tracklib/kit'
import Cover from 'components/collection/cover'
import PlayButton from 'components/playbutton'
import LoadMoreButton from 'components/base/loadmorebutton'

// Hooks
import { useRequest } from 'modules/requests/hooks'
import { useCollections } from 'modules/entities/hooks'
import { useFavoritesCollectionId } from 'modules/collections/hooks'

// Types
import { Collection as CollectionType, QueryResult, State } from 'types'

// Utils
import { isBrowser } from '@process'

import styles from './grid.css'

type CollectionProps = {
  collection: CollectionType
}

export const Collection = ({ collection }: CollectionProps): JSX.Element => {
  const image = <Cover collection={collection} className={styles.cover} />
  const url = `/collection/${collection.id}`
  return (
    <div className={styles.collectionContainer}>
      <Entity
        title={collection.title}
        description={collection.description || `by ${collection.owner_name}`}
        image={image}
        backgroundColor="transparent"
        url={url}
        hoverComponent={
          <PlayButton entity="collection" value={String(collection.id)} contextKey={`collection-${collection.id}`} hoverOnly />
        }
        datatesting="entity_collection"
        overflowVisible={!!collection.crate}
      />
    </div>
  )
}

const ROWS = 5
const COLUMNS = 5
const ENTITIES_NR = ROWS * COLUMNS

type GridProps = {
  searchQuery: string
  emptyStateTitle?: string
  emptyStateSubtitle?: string
  heading?: string
}

const Grid = ({ searchQuery, emptyStateTitle = 'No matching collections found', emptyStateSubtitle, heading }: GridProps): JSX.Element => {
  const dispatch = useDispatch()
  const fetchCollectionsRequest = useRequest('FETCH_COLLECTIONS', searchQuery)
  const allCollections = useCollections()
  const searchInfo = useSelector<State, QueryResult>(state => state.collectionsSearch[searchQuery])
  const favoritesCollectionId = useFavoritesCollectionId()
  const collections =
    searchInfo && Object.keys(allCollections).length
      ? searchInfo.ids.filter(x => x !== favoritesCollectionId).map(id => allCollections[String(id)])
      : []
  const showLoadMoreButton = collections.length > 0 && collections.length < searchInfo?.count - 1 // subtract favorites collection

  useEffect(() => {
    if (!fetchCollectionsRequest.isLoading && !fetchCollectionsRequest.hasError) {
      dispatch(collectionsActions.fetchCollections(searchQuery, 1, ENTITIES_NR))
    }
  }, [searchQuery]) // eslint-disable-line

  return (
    <>
      {heading && <h2 className={styles.heading}>{heading}</h2>}
      {fetchCollectionsRequest.isDone && collections.length === 0 && (
        <EmptyState
          text={emptyStateTitle}
          subtitle={emptyStateSubtitle}
          emoji={<IconCollectionLarge className={styles.emptyStateIcon} />}
        />
      )}
      <div className={styles.grid}>
        {collections.length > 0 && collections.map(c => <Collection key={c.id} collection={c} />)}
        {(fetchCollectionsRequest.isLoading || !isBrowser) &&
          Array.from({ length: ENTITIES_NR }, (v, k) => k + 1).map(index => (
            <div className={styles.collectionContainer} key={String(index)}>
              <Entity loading datatesting="entity_loading" />
            </div>
          ))}
      </div>
      {showLoadMoreButton && (
        <LoadMoreButton
          disabled={fetchCollectionsRequest.isLoading}
          onClick={() => dispatch(collectionsActions.fetchCollections(searchQuery, searchInfo.lastPageLoaded + 1, ENTITIES_NR))}
          datatesting="button_collections_load_more"
          noPad
        >
          Load more collections
        </LoadMoreButton>
      )}
    </>
  )
}

export default Grid
